import Navbar from "../components/Navbar";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import {
  clampDiscountRate,
  resolveItemDisplayVariantName,
  resolveItemUnitPrice,
  resolveProductImageUrl,
} from "../utils/commerce";
import "./Checkout.css";

const LOCAL_COUNTRY = "Brunei Darussalam";
const DISTRICT_OPTIONS = ["Brunei Muara", "Tutong", "Belait", "Temburong"];

const splitStreetAddress = (street = "") => {
  const raw = String(street || "").trim();
  if (!raw) return { streetLine1: "", streetLine2: "" };
  const parts = raw.split(" | ");
  if (parts.length > 1) {
    return {
      streetLine1: (parts[0] || "").trim(),
      streetLine2: parts.slice(1).join(" | ").trim(),
    };
  }
  return { streetLine1: raw, streetLine2: "" };
};

const composeStreetAddress = (streetLine1 = "", streetLine2 = "") =>
  [streetLine1.trim(), streetLine2.trim()].filter(Boolean).join(" | ");

const CARD_NUMBER_REGEX = /^\d{16}$/;
const EXPIRY_REGEX = /^(0[1-9]|1[0-2])\/(\d{2})$/;
const CVV_REGEX = /^\d{3,4}$/;
const EMPTY_CARD_DETAILS = {
  cardNumber: "",
  cardName: "",
  expiryDate: "",
  cvv: "",
};

const normalizeCardNumber = (value) => value.replace(/\D/g, "").slice(0, 16);
const normalizeExpiry = (value) => {
  const raw = value.replace(/[^\d]/g, "").slice(0, 4);
  if (raw.length <= 2) return raw;
  return `${raw.slice(0, 2)}/${raw.slice(2)}`;
};

const validateCardForm = (cardForm) => {
  const errors = {};
  const cardNumber = normalizeCardNumber(cardForm.cardNumber);
  const cardName = cardForm.cardName.trim();
  const expiryDate = normalizeExpiry(cardForm.expiryDate);

  if (!cardNumber) errors.cardNumber = "Card number is required";
  else if (!CARD_NUMBER_REGEX.test(cardNumber))
    errors.cardNumber = "Must be 16 digits";

  if (!cardName) errors.cardName = "Cardholder name is required";

  if (!expiryDate) errors.expiryDate = "Expiry date is required";
  else if (!EXPIRY_REGEX.test(expiryDate)) errors.expiryDate = "Format: MM/YY";

  if (!CVV_REGEX.test(cardForm.cvv || "")) errors.cvv = "3-4 digits required";

  return errors;
};

const PaymentIcon = ({ icon }) => {
  if (icon === "cash") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
};

export default function Checkout() {
  const navigate = useNavigate();
  const paymentMethods = [
    { value: "cash_on_delivery", label: "Cash on Delivery", icon: "cash" },
    { value: "card", label: "Credit / Debit Card", icon: "card" },
  ];

  const [summary, setSummary] = useState({
    total: 0,
    discount: 0,
    finalTotal: 0,
  });
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [showPaymentPicker, setShowPaymentPicker] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState("cash_on_delivery");
  const [cardDetails, setCardDetails] = useState({
    cardNumber: "",
    cardName: "",
    expiryDate: "",
    cvv: "",
  });
  const [cardErrors, setCardErrors] = useState({});
  const [addingAddress, setAddingAddress] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: "Home",
    receiverName: "",
    phone: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    state: "",
    postalCode: "",
  });

  useEffect(() => {
    document.title = "Checkout | Balitah";
  }, []);

  useEffect(() => {
    const loadCheckoutData = async () => {
      try {
        const cartData = await api.getCart();
        const items = (cartData.data?.items || []).filter(
          (item) => item.selected !== false,
        );
        setCartItems(items);
        const total = items.reduce(
          (sum, item) => sum + resolveItemUnitPrice(item) * item.quantity,
          0,
        );
        const discount = items.reduce((sum, item) => {
          const basePrice = resolveItemUnitPrice(item);
          const discountedPrice =
            basePrice *
            (1 - clampDiscountRate(item.product.discountRate) / 100);
          return sum + (basePrice - discountedPrice) * item.quantity;
        }, 0);
        setSummary({ total, discount, finalTotal: total - discount });
      } catch (error) {
        console.error("Failed to load cart:", error);
        alert("Failed to load cart");
      }

      try {
        const res = await api.getAddresses();
        if (res.success) {
          const list = res.data.addresses || [];
          setAddresses(list);
          if (list.length > 0) setSelectedAddressId(String(list[0]._id));
        }
      } catch (error) {
        console.error("Failed to load addresses:", error);
      }
    };

    loadCheckoutData();
  }, []);

  const selectedAddress = addresses.find(
    (a) => String(a._id) === selectedAddressId,
  );
  const selectedAddressStreet = splitStreetAddress(
    selectedAddress?.street || "",
  );
  const selectedPayment = paymentMethods.find(
    (m) => m.value === selectedPaymentMethod,
  );

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setAddingAddress(true);

    const payload = {
      ...newAddress,
      street: composeStreetAddress(
        newAddress.streetLine1,
        newAddress.streetLine2,
      ),
      country: LOCAL_COUNTRY,
    };

    const res = await api.addAddress(payload);
    setAddingAddress(false);

    if (!res.success) {
      alert(res.message || "Failed to add address");
      return;
    }

    const list = res.data.addresses || [];
    const created = res.data.newAddress;
    setAddresses(list);
    if (created?._id) setSelectedAddressId(String(created._id));
    setShowAddAddress(false);
    setShowAddressPicker(false);
    setNewAddress({
      label: "Home",
      receiverName: "",
      phone: "",
      streetLine1: "",
      streetLine2: "",
      city: "",
      state: "",
      postalCode: "",
    });
  };

  const handleCheckout = async () => {
    if (!selectedAddress) {
      alert("Please select a shipping address first");
      setShowAddressPicker(true);
      return;
    }

    if (summary.total <= 0) {
      alert("Please select at least one item in the cart");
      return;
    }

    if (!selectedAddress.receiverName || !selectedAddress.phone) {
      alert("Selected address is missing receiver name or phone number");
      setShowAddressPicker(true);
      return;
    }

    if (selectedPaymentMethod === "card") {
      const errors = validateCardForm(cardDetails);
      if (Object.keys(errors).length > 0) {
        setCardErrors(errors);
        alert("Please complete the card details");
        return;
      }
    }

    setPlacingOrder(true);

    const res = await api.placeOrder({
      shippingAddress: {
        label: selectedAddress.label || "Home",
        receiverName: selectedAddress.receiverName,
        phone: selectedAddress.phone,
        street: selectedAddress.street,
        city: selectedAddress.city,
        state: selectedAddress.state,
        postalCode: selectedAddress.postalCode,
        country: selectedAddress.country || LOCAL_COUNTRY,
      },
      paymentMethod: selectedPaymentMethod,
      paymentCard:
        selectedPaymentMethod === "card"
          ? {
              cardNumber: normalizeCardNumber(cardDetails.cardNumber),
              cardName: cardDetails.cardName.trim(),
              expiryDate: normalizeExpiry(cardDetails.expiryDate),
              cvv: cardDetails.cvv,
            }
          : undefined,
    });

    setPlacingOrder(false);

    if (res.success) {
      alert("Order placed successfully!");
      navigate("/orders");
    } else {
      alert(res.message);
    }
  };

  return (
    <div className="co-page">
      <Navbar />

      <div className="co-container">
        {/* Back Button */}
        <button className="co-back-btn" onClick={() => navigate("/cart")}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Cart
        </button>

        <div className="co-header">
          <h1>Checkout</h1>
          <p>Complete your order</p>
        </div>

        <div className="co-content">
          {/* Left Column */}
          <div className="co-main">
            {/* Shipping Address Section */}
            <div className="co-section">
              <div className="co-section-header">
                <div className="co-section-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="co-section-title">
                  <h2>Shipping Address</h2>
                  <span className="co-step-badge">Step 1</span>
                </div>
              </div>

              {selectedAddress ? (
                <div className="co-address-display">
                  <div className="co-address-info">
                    <div className="co-address-top">
                      <span className="co-address-label">
                        {selectedAddress.label}
                      </span>
                      <span className="co-address-name">
                        {selectedAddress.receiverName}
                      </span>
                    </div>
                    <p className="co-address-phone">{selectedAddress.phone}</p>
                    <p className="co-address-street">
                      {selectedAddressStreet.streetLine1}
                      {selectedAddressStreet.streetLine2 && (
                        <>, {selectedAddressStreet.streetLine2}</>
                      )}
                    </p>
                    <p className="co-address-city">
                      {selectedAddress.city}, {selectedAddress.state}{" "}
                      {selectedAddress.postalCode}
                    </p>
                  </div>
                  <button
                    className="co-change-btn"
                    onClick={() => setShowAddressPicker(true)}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="co-empty-address">
                  <p>No shipping address selected</p>
                  <button
                    className="co-btn co-btn-outline"
                    onClick={() => setShowAddressPicker(true)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Address
                  </button>
                </div>
              )}
            </div>

            {/* Payment Method Section */}
            <div className="co-section">
              <div className="co-section-header">
                <div className="co-section-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <div className="co-section-title">
                  <h2>Payment Method</h2>
                  <span className="co-step-badge">Step 2</span>
                </div>
              </div>

              <div className="co-payment-display">
                <div className="co-payment-selected">
                  <div className={`co-payment-icon ${selectedPayment?.icon}`}>
                    <PaymentIcon icon={selectedPayment?.icon} />
                  </div>
                  <span>{selectedPayment?.label || "Select payment"}</span>
                </div>
                <button
                  className="co-change-btn"
                  onClick={() => setShowPaymentPicker(true)}
                >
                  Change
                </button>
              </div>

              {/* Card Details Form */}
              {selectedPaymentMethod === "card" && (
                <div className="co-card-form">
                  <div className="co-form-group">
                    <label htmlFor="co-card-number">Card Number</label>
                    <input
                      id="co-card-number"
                      className={`co-input ${cardErrors.cardNumber ? "error" : ""}`}
                      placeholder="1234 5678 9012 3456"
                      inputMode="numeric"
                      maxLength="16"
                      value={cardDetails.cardNumber}
                      onChange={(e) =>
                        setCardDetails((prev) => ({
                          ...prev,
                          cardNumber: normalizeCardNumber(e.target.value),
                        }))
                      }
                    />
                    {cardErrors.cardNumber && (
                      <span className="co-field-error">
                        {cardErrors.cardNumber}
                      </span>
                    )}
                  </div>

                  <div className="co-form-group">
                    <label htmlFor="co-card-name">Cardholder Name</label>
                    <input
                      id="co-card-name"
                      className={`co-input ${cardErrors.cardName ? "error" : ""}`}
                      placeholder="Name on card"
                      value={cardDetails.cardName}
                      onChange={(e) =>
                        setCardDetails((prev) => ({
                          ...prev,
                          cardName: e.target.value,
                        }))
                      }
                    />
                    {cardErrors.cardName && (
                      <span className="co-field-error">
                        {cardErrors.cardName}
                      </span>
                    )}
                  </div>

                  <div className="co-form-row">
                    <div className="co-form-group">
                      <label htmlFor="co-expiry">Expiry Date</label>
                      <input
                        id="co-expiry"
                        className={`co-input ${cardErrors.expiryDate ? "error" : ""}`}
                        placeholder="MM/YY"
                        value={cardDetails.expiryDate}
                        onChange={(e) =>
                          setCardDetails((prev) => ({
                            ...prev,
                            expiryDate: normalizeExpiry(e.target.value),
                          }))
                        }
                      />
                      {cardErrors.expiryDate && (
                        <span className="co-field-error">
                          {cardErrors.expiryDate}
                        </span>
                      )}
                    </div>

                    <div className="co-form-group">
                      <label htmlFor="co-cvv">CVV</label>
                      <input
                        id="co-cvv"
                        className={`co-input ${cardErrors.cvv ? "error" : ""}`}
                        placeholder="123"
                        type="password"
                        maxLength="4"
                        autoComplete="cc-csc"
                        value={cardDetails.cvv}
                        onChange={(e) =>
                          setCardDetails((prev) => ({
                            ...prev,
                            cvv: e.target.value.replace(/\D/g, "").slice(0, 4),
                          }))
                        }
                      />
                      {cardErrors.cvv && (
                        <span className="co-field-error">{cardErrors.cvv}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Items Section */}
            <div className="co-section">
              <div className="co-section-header">
                <div className="co-section-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 0 1-8 0" />
                  </svg>
                </div>
                <div className="co-section-title">
                  <h2>Order Items</h2>
                  <span className="co-item-count">
                    {cartItems.length} item{cartItems.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {cartItems.length > 0 ? (
                <div className="co-items-list">
                  {cartItems.map((item) => {
                    const discountRate = clampDiscountRate(
                      item.product.discountRate,
                    );
                    const hasDiscount = discountRate > 0;
                    const imageUrl = resolveProductImageUrl(item.product, {
                      sortByOrder: true,
                    });
                    const unitPrice = resolveItemUnitPrice(item);
                    const discountedUnitPrice =
                      unitPrice * (1 - discountRate / 100);
                    const variantName = resolveItemDisplayVariantName(item);

                    return (
                      <div
                        key={`${item.product._id}-${item.variantName || "default"}`}
                        className="co-item"
                      >
                        <div className="co-item-image">
                          {imageUrl ? (
                            <img src={imageUrl} alt={item.product.name} />
                          ) : (
                            <div className="co-no-image">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              >
                                <rect
                                  x="3"
                                  y="3"
                                  width="18"
                                  height="18"
                                  rx="2"
                                  ry="2"
                                />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="co-item-info">
                          <h3>{item.product.name}</h3>
                          {variantName && (
                            <p className="co-item-type">{variantName}</p>
                          )}
                          <p className="co-item-qty">Qty: {item.quantity}</p>
                        </div>
                        <div className="co-item-price">
                          {hasDiscount ? (
                            <>
                              <span className="co-price-discounted">
                                BND{" "}
                                {(discountedUnitPrice * item.quantity).toFixed(
                                  2,
                                )}
                              </span>
                              <span className="co-price-original">
                                BND {(unitPrice * item.quantity).toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="co-price-discounted">
                              BND {(unitPrice * item.quantity).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="co-no-items">No items selected for checkout.</p>
              )}
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="co-sidebar">
            <div className="co-summary-card">
              <h2>Order Summary</h2>

              <div className="co-summary-rows">
                <div className="co-summary-row">
                  <span>Subtotal ({cartItems.length} items)</span>
                  <span>BND {summary.total.toFixed(2)}</span>
                </div>
                {summary.discount > 0 && (
                  <div className="co-summary-row co-discount">
                    <span>Discount</span>
                    <span>-BND {summary.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="co-summary-row">
                  <span>Shipping</span>
                  <span className="co-free">Free</span>
                </div>
              </div>

              <div className="co-summary-total">
                <span>Total</span>
                <span>BND {summary.finalTotal.toFixed(2)}</span>
              </div>

              <button
                className="co-btn co-btn-checkout"
                onClick={handleCheckout}
                disabled={placingOrder || cartItems.length === 0}
              >
                {placingOrder ? (
                  <>
                    <div className="co-btn-spinner"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    Place Order
                  </>
                )}
              </button>

              <p className="co-secure-note">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Your payment info is secure
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Address Picker Modal */}
      {showAddressPicker && (
        <div
          className="co-modal-backdrop"
          onClick={() => setShowAddressPicker(false)}
        >
          <div className="co-modal" onClick={(e) => e.stopPropagation()}>
            <div className="co-modal-header">
              <h3>Select Address</h3>
              <button
                className="co-modal-close"
                onClick={() => setShowAddressPicker(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="co-modal-body">
              {addresses.length === 0 ? (
                <p className="co-no-addresses">No saved addresses yet.</p>
              ) : (
                <div className="co-address-list">
                  {addresses.map((address) => (
                    <label
                      key={address._id}
                      className={`co-address-option ${String(address._id) === selectedAddressId ? "selected" : ""}`}
                    >
                      <input
                        type="radio"
                        checked={String(address._id) === selectedAddressId}
                        onChange={() =>
                          setSelectedAddressId(String(address._id))
                        }
                      />
                      <div className="co-radio-custom"></div>
                      <div className="co-address-option-info">
                        <div className="co-address-option-top">
                          <span className="co-option-label">
                            {address.label}
                          </span>
                          <span className="co-option-name">
                            {address.receiverName}
                          </span>
                        </div>
                        <p>{address.phone}</p>
                        <p>
                          {splitStreetAddress(address.street).streetLine1}
                          {splitStreetAddress(address.street).streetLine2 &&
                            `, ${splitStreetAddress(address.street).streetLine2}`}
                        </p>
                        <p>
                          {address.city}, {address.state} {address.postalCode}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="co-modal-footer">
              <button
                className="co-btn co-btn-outline"
                onClick={() => setShowAddAddress(true)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Address
              </button>
              <button
                className="co-btn co-btn-primary"
                onClick={() => setShowAddressPicker(false)}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Picker Modal */}
      {showPaymentPicker && (
        <div
          className="co-modal-backdrop"
          onClick={() => setShowPaymentPicker(false)}
        >
          <div
            className="co-modal co-modal-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="co-modal-header">
              <h3>Payment Method</h3>
              <button
                className="co-modal-close"
                onClick={() => setShowPaymentPicker(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="co-modal-body">
              <div className="co-payment-list">
                {paymentMethods.map((method) => (
                  <label
                    key={method.value}
                    className={`co-payment-option ${selectedPaymentMethod === method.value ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      checked={selectedPaymentMethod === method.value}
                      onChange={() => {
                        setSelectedPaymentMethod(method.value);
                        if (method.value !== "card") {
                          setCardDetails(EMPTY_CARD_DETAILS);
                          setCardErrors({});
                        }
                      }}
                    />
                    <div className="co-radio-custom"></div>
                    <div className={`co-payment-option-icon ${method.icon}`}>
                      <PaymentIcon icon={method.icon} />
                    </div>
                    <span>{method.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="co-modal-footer">
              <button
                className="co-btn co-btn-primary"
                onClick={() => setShowPaymentPicker(false)}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Address Modal */}
      {showAddAddress && (
        <div
          className="co-modal-backdrop"
          onClick={() => setShowAddAddress(false)}
        >
          <div className="co-modal" onClick={(e) => e.stopPropagation()}>
            <div className="co-modal-header">
              <h3>Add New Address</h3>
              <button
                className="co-modal-close"
                onClick={() => setShowAddAddress(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form className="co-modal-body" onSubmit={handleAddAddress}>
              <div className="co-form-group">
                <label>Label</label>
                <input
                  className="co-input"
                  placeholder="Home / Office"
                  value={newAddress.label}
                  onChange={(e) =>
                    setNewAddress((prev) => ({
                      ...prev,
                      label: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="co-form-row">
                <div className="co-form-group">
                  <label>Receiver Name *</label>
                  <input
                    className="co-input"
                    placeholder="Full name"
                    value={newAddress.receiverName}
                    onChange={(e) =>
                      setNewAddress((prev) => ({
                        ...prev,
                        receiverName: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div className="co-form-group">
                  <label>Phone Number *</label>
                  <input
                    className="co-input"
                    placeholder="Phone number"
                    value={newAddress.phone}
                    onChange={(e) =>
                      setNewAddress((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
              </div>

              <div className="co-form-group">
                <label>Street Address *</label>
                <input
                  className="co-input"
                  placeholder="Street address line 1"
                  value={newAddress.streetLine1}
                  onChange={(e) =>
                    setNewAddress((prev) => ({
                      ...prev,
                      streetLine1: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="co-form-group">
                <label>Street Address 2</label>
                <input
                  className="co-input"
                  placeholder="Apt, suite, unit (optional)"
                  value={newAddress.streetLine2}
                  onChange={(e) =>
                    setNewAddress((prev) => ({
                      ...prev,
                      streetLine2: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="co-form-row">
                <div className="co-form-group">
                  <label>City *</label>
                  <input
                    className="co-input"
                    placeholder="City"
                    value={newAddress.city}
                    onChange={(e) =>
                      setNewAddress((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div className="co-form-group">
                  <label>State *</label>
                  <select
                    className="co-input"
                    value={newAddress.state}
                    onChange={(e) =>
                      setNewAddress((prev) => ({
                        ...prev,
                        state: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select district</option>
                    {DISTRICT_OPTIONS.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="co-form-group" style={{ maxWidth: "200px" }}>
                <label>Postal Code *</label>
                <input
                  className="co-input"
                  placeholder="Postal code"
                  value={newAddress.postalCode}
                  onChange={(e) =>
                    setNewAddress((prev) => ({
                      ...prev,
                      postalCode: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="co-modal-footer">
                <button
                  className="co-btn co-btn-outline"
                  type="button"
                  onClick={() => setShowAddAddress(false)}
                >
                  Cancel
                </button>
                <button
                  className="co-btn co-btn-primary"
                  type="submit"
                  disabled={addingAddress}
                >
                  {addingAddress ? "Saving..." : "Save Address"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
